import React, { useReducer, useEffect } from 'react'
import { Flex, IconButton, List, Item, EmployeeSelect, Toaster } from '@ui'
import { Classes } from '@blueprintjs/core'
import api, { IEmployee, IManagedEmployee, Role } from '@api'
import { sortBy } from 'lodash'

enum Actions {
  LOAD,
  UPDATE,
}

interface IState {
  reqId: number
  loading: boolean
  managedEmployees: IManagedEmployee[]
}

interface Action {
  type: Actions.LOAD | Actions.UPDATE
  reqId: number
  managedEmployees: IManagedEmployee[]
}

export default function ManagedEmployeesList({
  readOnly,
  employeeId,
  role,
  updateEmployeeList,
  deptIds = [],
  bookingManagerSetting,
}: {
  readOnly: boolean
  employeeId: string
  role: string
  updateEmployeeList: (empIds: string[]) => void
  deptIds: any
  bookingManagerSetting: any
}) {
  const { loading, managedEmployees, addEmployee, deleteEmployee, reset } = useManagedEmployees(
    employeeId,
    role,
    updateEmployeeList,
    bookingManagerSetting
  )

  if (loading) {
    return (
      <List style={{ marginTop: 10 }}>
        <Item>
          <span className={Classes.SKELETON}>Placeholder text</span>
        </Item>
        <Item>
          <span className={Classes.SKELETON}>Placeholder text</span>
        </Item>
        <Item>
          <span className={Classes.SKELETON}>Placeholder text</span>
        </Item>
      </List>
    )
  }

  // Do not show mangeged departement section when list is empty
  if (readOnly && !managedEmployees?.length) {
    return null
  }

  const onChangeEmployee = (reqId: number, message: string) => {
    const empIds = managedEmployees?.map(({ employee }) => employee.id)
    updateEmployeeList(empIds)
    api.employee
      .updateDeptEmpForBookingManager({
        id: employeeId,
        bookingsManagerSetting: {
          managed_departments_ids: deptIds,
          managed_employees_ids: empIds,
          manage_all: false,
        },
        role: role,
      })
      .then(() => {
        const updatedArray = managedEmployees?.map((emp: any) => {
          return { employee: emp?.employee }
        })
        reset(updatedArray, reqId)
        Toaster.success({ message: message })
      })
  }
  return (
    <>
      <List title="Managed employees" style={{ marginTop: 10 }}>
        {(sortBy(managedEmployees, 'employee.name') as IManagedEmployee[]).map(({ employee }) => (
          <Item key={employee.id}>
            <Flex align="center" justify="space-between">
              <div style={{ fontSize: 15 }}>{employee.name} </div>
              {!readOnly && (
                <IconButton
                  small
                  minimal
                  tooltip="Remove"
                  iconName="times"
                  className="icon-button"
                  onClick={() => {
                    const reqId = Date.now()
                    deleteEmployee(employee, reqId)
                    onChangeEmployee(reqId, 'Employee deleted')
                  }}
                />
              )}
            </Flex>
          </Item>
        ))}
        {!readOnly && (
          <Item>
            <EmployeeSelect
              value={null}
              onChange={(employee: any) => {
                const reqId = Date.now()
                const isEmployeeUnchecked = managedEmployees?.find(
                  (emp) => emp.employee.id === employee.id
                )
                if (isEmployeeUnchecked) {
                  deleteEmployee(employee, reqId)
                  onChangeEmployee(reqId, 'Employee deleted')
                } else {
                  addEmployee(employee, reqId)
                  onChangeEmployee?.(reqId, 'Employee added')
                }
              }}
              placeholder="Add Employee"
              buttonProps={{
                disabled: false,
                iconName: 'plus',
                'data-button': 'add_employee',
              }}
              multi={role === Role.BOOKINGS_MANAGER}
              itemDisabled={(emp) =>
                !!managedEmployees?.find(({ employee }) => employee.id === emp.id)
              }
            />
          </Item>
        )}
      </List>
    </>
  )
}

function useManagedEmployees(
  employeeId: string,
  role: string,
  updateEmployeeList: (empIds: string[]) => void,
  bookingManagerSetting: any
): IState & {
  addEmployee: (employee: IEmployee, reqId: number) => void
  deleteEmployee: (employee: IEmployee, reqId: number) => void
  reset: (managedEmployees: IManagedEmployee[], reqId: number) => void
} {
  const [state, dispatch] = useReducer(reducer, {
    reqId: 0,
    loading: true,
    managedEmployees: [],
  })

  useEffect(() => {
    const reqId = Date.now()
    let updatedArray: any = []
    setTimeout(() => {
      updatedArray = bookingManagerSetting?.managed_employees?.map((emp: any) => {
        return { employee: emp }
      })
      const empIds = updatedArray?.map(({ employee }: any) => employee.id)
      updateEmployeeList(empIds)
      dispatch({
        type: Actions.LOAD,
        managedEmployees: updatedArray || [],
        reqId,
      })
    })
  }, [employeeId, role, bookingManagerSetting])

  return {
    ...state,
    addEmployee: (employee: IEmployee, reqId: number) => {
      const exists = state.managedEmployees?.find(({ employee: { id } }) => employee.id === id)

      if (exists) {
        return
      } else {
        state.managedEmployees?.push({ employee })
      }

      dispatch({
        type: Actions.UPDATE,
        managedEmployees: state.managedEmployees,
        reqId,
      })
    },

    deleteEmployee: (employee: IEmployee, reqId: number) => {
      const iIndex = state.managedEmployees?.findIndex(({ employee: { id } }) => employee.id === id)
      state.managedEmployees?.splice(iIndex, 1)
      dispatch({
        type: Actions.UPDATE,
        managedEmployees: state.managedEmployees,
        reqId,
      })
    },

    reset: (managedEmployees, reqId) => {
      dispatch({
        type: Actions.LOAD,
        managedEmployees,
        reqId,
      })
    },
  }
}

function reducer(state: IState, action: Action) {
  if (state.reqId > action.reqId) {
    return state
  }

  switch (action.type) {
    case Actions.LOAD:
      return {
        loading: false,
        reqId: action.reqId,
        managedEmployees: action.managedEmployees,
      }

    case Actions.UPDATE:
      return {
        ...state,
        reqId: action.reqId,
        managedEmployees: action.managedEmployees,
      }

    default:
      return state
  }
}
